import { graphql, fetchQuery } from 'react-relay';
import tracking from 'dibs-tracking';
import md5 from 'md5';
import { getEverLoggedIn, getBuyerId, getBuyerToken, isMasquerading } from 'dibs-cookie-jar';
import { localStorage, sessionStorage } from 'dibs-browser-storage';
import { trackPreRegistrationPageVisit } from 'dibs-visit-tracking/exports/sourceAttribution';

import { unmaskFragmentData } from '../helpers/relayUnmaskingHelper';
import { getIncomeLevelInfo } from './userGeoInfo';
import { handleGuestId, getGuestId } from './guestIdStorage';
import {
    isTradeCampaign,
    isBuyerCampaign,
    getEmailTokenFromQuery,
} from '../helpers/queryParamsHelpers';
import { formatDate } from '../helpers/trackingHelpers';

const USER_EMAIL_TOKEN = 'userEmailToken';
const USER_EMAIL = 'userEmail';
const STORAGE_TRANSACTIONS_STATUS = 'userTransactionsStatus';
const STORAGE_CHECKOUT_STATUS = 'userCheckoutStatus';
const STORAGE_TRADE_FIRM_ID = 'tradeFirmId';
const BUYER_STATUS_NONE = 'NONE';
const BUYER_STATUS_CONFIRMED_MULTIPLE = 'CONFIRMED_MULTIPLE';
const BUYER_STATUS_CONFIRMED_ONE = 'CONFIRMED_ONE';
const BUYER_STATUS_SUBMITTED = 'SUBMITTED';
const BUYER_STATUS_SUBMITTED_DIBS_CAMPAIGN = 'SUBMITTED_DIBS_CAMPAIGN';
const BUYER_STATUSES_MAP = {
    [BUYER_STATUS_CONFIRMED_MULTIPLE]: 'submitted|multiple confirmed',
    [BUYER_STATUS_CONFIRMED_ONE]: 'submitted|one confirmed',
    [BUYER_STATUS_SUBMITTED]: 'submitted|none confirmed',
    [BUYER_STATUS_SUBMITTED_DIBS_CAMPAIGN]: 'submitted|unknown confirmed',
    [BUYER_STATUS_NONE]: 'no orders',
};

export const CUSTOMER_TYPE_TRADE = 'Trade';
export const CUSTOMER_TYPE_VIP = 'VIP';
export const CUSTOMER_TYPE_CONSUMER = 'Consumer';
export const CUSTOMER_TYPE_UNKNOWN = 'Unknown';
export const GUEST_CHECKOUT = 'guestCheckout';
const HAS_GUEST_CHECKOUT = 'hasGuestCheckout';

/**
 * Gets guest user info from session.
 * It get's stored after transaction is created for guest user in checkout page
 * Stored data: userId, email, zipCode, isNewUser
 */
export const getGuestUserSessionInfo = () => sessionStorage.getItem(GUEST_CHECKOUT) || {};

/*
 * depending on transaction history, determine which value to add for the BUYER_STATUSES_MAP
 */
function getBuyerStatusFromTransactions(transactions) {
    const { submittedTransactions, confirmedTransactions } = transactions;
    let buyerStatus;

    if (confirmedTransactions && confirmedTransactions > 1) {
        buyerStatus = BUYER_STATUS_CONFIRMED_MULTIPLE;
    } else if (confirmedTransactions) {
        buyerStatus = BUYER_STATUS_CONFIRMED_ONE;
    } else if (submittedTransactions) {
        buyerStatus = BUYER_STATUS_SUBMITTED;
    } else {
        buyerStatus = BUYER_STATUS_NONE;
    }

    return { buyerStatus };
}

function getUserTransactionsFromLocalStorage() {
    return (
        sessionStorage.getItem(STORAGE_TRANSACTIONS_STATUS) ||
        localStorage.getItem(STORAGE_TRANSACTIONS_STATUS)
    );
}

function setUserTransactionStatusToLocalStorage(status) {
    sessionStorage.setItem(STORAGE_TRANSACTIONS_STATUS, { buyerStatus: status });
    localStorage.setItem(STORAGE_TRANSACTIONS_STATUS, { buyerStatus: status });
}

function setGuestCheckoutUserTransactionStatusToLocalStorage(status) {
    localStorage.setItem(HAS_GUEST_CHECKOUT, status);
}

function getGuestCheckoutUserTransactionStatusFromLocalStorage() {
    return localStorage.getItem(HAS_GUEST_CHECKOUT);
}

function getGuestCheckoutTransactions(transactions) {
    const { guestCheckoutTransactions } = transactions || {};
    let hasGuestCheckout;
    if (typeof guestCheckoutTransactions === 'number') {
        hasGuestCheckout = guestCheckoutTransactions > 0 ? 'Y' : 'N';
        setGuestCheckoutUserTransactionStatusToLocalStorage(hasGuestCheckout);
    } else {
        hasGuestCheckout = getGuestCheckoutUserTransactionStatusFromLocalStorage();
    }
    return hasGuestCheckout || 'N';
}

/*
 * fetches all transactions created by user using different filters
 * in order to track order history
 */
export function getTransactionsStatus({ transactions = {}, resetStorage = false }) {
    let transactionsStatus = getUserTransactionsFromLocalStorage();
    /**
     * Transactions status is always being taken from a session storage.
     * When user logs in or registers, this storage entry is set again.
     */
    if (!transactionsStatus || resetStorage) {
        transactionsStatus = getBuyerStatusFromTransactions(transactions);
        setUserTransactionStatusToLocalStorage(transactionsStatus.buyerStatus);
    }

    if (transactionsStatus) {
        transactionsStatus.buyerStatus = BUYER_STATUSES_MAP[transactionsStatus.buyerStatus];
    }

    return transactionsStatus;
}

/*
 *   get current account type, track it on modal change (close) event
 */
export function getCurrentAccountType({
    user,
    passwordAdded,
    accountCreated,
    loggedIn,
    socialLogin,
}) {
    const authMetadata = user?.authMetadata;
    const emailOnly = authMetadata?.emailOnly;
    const hasPassword = authMetadata?.hasPassword;
    let currentAccountType;

    if (emailOnly || accountCreated) {
        currentAccountType = 'email login|email account';
    } else if (hasPassword || passwordAdded || loggedIn || socialLogin) {
        currentAccountType = 'email and password login|full account';
    }

    return currentAccountType;
}

/**
 * get buyer status based on dibs_campaign query param, f.ex. ?dibs_campaign=buyer
 * note: exported for testing
 */
export const getTransactionStatusFromQueryParams = () => {
    let buyerStatus;
    if (isBuyerCampaign()) {
        buyerStatus = BUYER_STATUSES_MAP[BUYER_STATUS_SUBMITTED_DIBS_CAMPAIGN];
    }
    if (buyerStatus) {
        setUserTransactionStatusToLocalStorage(BUYER_STATUS_SUBMITTED_DIBS_CAMPAIGN);
    }
    return { buyerStatus };
};

/**
 * gets stored transactions status for logged out users
 */
function getStoredTransactionsStatus() {
    const transactionsStatus = getUserTransactionsFromLocalStorage();
    if (transactionsStatus && transactionsStatus.buyerStatus) {
        transactionsStatus.buyerStatus = BUYER_STATUSES_MAP[transactionsStatus.buyerStatus];
        return transactionsStatus;
    } else if (!transactionsStatus) {
        return getTransactionStatusFromQueryParams();
    }
    return {
        buyerStatus: BUYER_STATUSES_MAP[BUYER_STATUS_NONE],
    };
}
function getGuestCheckoutUserTransactionStatus() {
    let buyerStatus = '';

    const isNewGuestCheckoutUser = getGuestUserSessionInfo().isNewUser;
    const transactionsStatus = getUserTransactionsFromLocalStorage() || {};
    if (isNewGuestCheckoutUser || transactionsStatus.buyerStatus === BUYER_STATUS_NONE) {
        buyerStatus = BUYER_STATUS_SUBMITTED;
    } else if (!transactionsStatus) {
        buyerStatus = BUYER_STATUS_SUBMITTED_DIBS_CAMPAIGN;
    } else {
        buyerStatus = transactionsStatus.buyerStatus;
    }
    if (buyerStatus !== transactionsStatus.buyerStatus) {
        setUserTransactionStatusToLocalStorage(buyerStatus);
    }

    return buyerStatus ? BUYER_STATUSES_MAP[buyerStatus] : BUYER_STATUSES_MAP[BUYER_STATUS_NONE];
}
/*
 * checkoutStatus logic is identical to buyer status, except it is
 * updated on different events and has nothing to do with session storage
 */
export function getCheckoutStatus({ transactions = {} }) {
    const storedCheckoutStatus = localStorage.getItem(STORAGE_CHECKOUT_STATUS);
    const guestCheckoutUserSessionInfo = getGuestUserSessionInfo();
    const guestCheckoutUserId = guestCheckoutUserSessionInfo.userId;
    const isNewGuestCheckoutUser = guestCheckoutUserSessionInfo.isNewUser;
    //  is user logged in? - only logged in user have 'transactions'
    if (typeof transactions?.confirmedTransactions === 'number') {
        const checkoutStatus =
            BUYER_STATUSES_MAP[getBuyerStatusFromTransactions(transactions).buyerStatus];
        if (storedCheckoutStatus !== checkoutStatus) {
            localStorage.setItem(STORAGE_CHECKOUT_STATUS, checkoutStatus);
        }
        return checkoutStatus;
    } else if (guestCheckoutUserId) {
        let checkoutStatus = storedCheckoutStatus;
        if (
            isNewGuestCheckoutUser ||
            storedCheckoutStatus === BUYER_STATUSES_MAP[BUYER_STATUS_NONE]
        ) {
            checkoutStatus = BUYER_STATUSES_MAP[BUYER_STATUS_SUBMITTED];
        } else if (!storedCheckoutStatus) {
            checkoutStatus = BUYER_STATUSES_MAP[BUYER_STATUS_SUBMITTED_DIBS_CAMPAIGN];
        }

        if (storedCheckoutStatus !== checkoutStatus) {
            localStorage.setItem(STORAGE_CHECKOUT_STATUS, checkoutStatus);
        }
        return checkoutStatus;
    } else {
        const {
            buyerStatus: userCheckoutStatusFromQueryParams,
        } = getTransactionStatusFromQueryParams();
        if (userCheckoutStatusFromQueryParams) {
            localStorage.setItem(
                STORAGE_CHECKOUT_STATUS,
                BUYER_STATUSES_MAP[BUYER_STATUS_SUBMITTED_DIBS_CAMPAIGN]
            );
            return BUYER_STATUSES_MAP[BUYER_STATUS_SUBMITTED_DIBS_CAMPAIGN];
        }
        return storedCheckoutStatus || BUYER_STATUSES_MAP[BUYER_STATUS_NONE];
    }
}

/*
 * create the userType tag according to
    - user permissions
    - dibs_campaign=true (forcedTradeUserType)
 * VIP|Trade|Consumer|Unknown
 */

export function getCustomerType(user, forcedTradeUserType) {
    const guestCheckoutUserId = getGuestUserSessionInfo().userId;
    if (isTradeCampaign() || forcedTradeUserType) {
        // isTradeCampaign: typically from paid media campaigns
        // forcedTradeUserType: typically from emailToken returns user.isVerifiedTrade as true
        return CUSTOMER_TYPE_TRADE;
    } else if (user.serviceId) {
        const isTrade = user.isVerifiedTrade ? CUSTOMER_TYPE_TRADE : '';
        const hasVipPermission = user.isVip ? CUSTOMER_TYPE_VIP : '';
        return (
            `${isTrade}${isTrade && hasVipPermission ? ' and ' : ''}${hasVipPermission}` ||
            CUSTOMER_TYPE_CONSUMER
        );
    } else if (user.everLoggedIn && !guestCheckoutUserId) {
        switch (user.everLoggedIn) {
            case 'trade':
                return CUSTOMER_TYPE_TRADE;
            case 'vip':
                return CUSTOMER_TYPE_VIP;
            default:
                return CUSTOMER_TYPE_CONSUMER;
        }
    } else if (guestCheckoutUserId) {
        return CUSTOMER_TYPE_CONSUMER;
    } else {
        return CUSTOMER_TYPE_UNKNOWN;
    }
}

const gaCookie = /^_ga=/;
/**
 * @returns {(string|null)} - first ga session date for user
 */
export function getFirstUtmSessionDate(user = {}) {
    if (user.firstSessionDate) {
        return user.firstSessionDate;
    }

    if (typeof document.cookie !== 'string') {
        return null;
    }

    const session = document.cookie.split(';').filter(str => gaCookie.test(str.trim()));
    if (session.length === 1) {
        const [, valueStr] = session.pop().split('=');
        const values = (valueStr && valueStr.split('.')) || [];
        const value = values[3];
        if (value) {
            return formatDate(parseInt(values[3], 10) * 1000);
        }
    }
    return null;
}

function getEmailToken() {
    return getEmailTokenFromQuery() || localStorage.getItem(USER_EMAIL_TOKEN);
}

// note: exported only for testing
export function getUserIdFromEmailToken(emailToken) {
    if (emailToken) {
        return emailToken.split('_')[0];
    } else {
        return undefined;
    }
}

/*
 * store tradeFirmId for tracking trade user logout activities
 */
const setTradeFirmId = ({ tradeFirmId }) => {
    sessionStorage.setItem(STORAGE_TRADE_FIRM_ID, tradeFirmId);
    localStorage.setItem(STORAGE_TRADE_FIRM_ID, tradeFirmId);
};

/**
 * gets stored tradeFirmId for logged out users
 */
export const getStoredTradeFirmId = () => {
    return (
        sessionStorage.getItem(STORAGE_TRADE_FIRM_ID) || localStorage.getItem(STORAGE_TRADE_FIRM_ID)
    );
};

/* gets user buyerDisplayable active promotion code */

const getActivePromotion = user => {
    const activePromotion = user?.activePromotion || {};

    if (activePromotion.buyerDisplayable) {
        return activePromotion.campaignName;
    }
    return 'none';
};
/*
 * creates the user tracking data.
 */
function trackUserAttributes({
    transactions = {},
    user = {},
    forcedTradeUserType,
    regionalInfo,
    userWithEmailTokenOnly,
}) {
    const userProfile = user?.profile;
    const tradeFirm = user?.tradeFirm;
    const mentionMeTagParameters = user?.mentionMeTagParameters;

    const guestCheckoutUserSessionInfo = getGuestUserSessionInfo();
    const guestCheckoutUserId = guestCheckoutUserSessionInfo.userId;
    const emailToken = getEmailToken();
    const incomeRange = getIncomeLevelInfo(regionalInfo);
    const userServiceId = user?.serviceId;
    const userID = userServiceId || getUserIdFromEmailToken(emailToken) || guestCheckoutUserId;
    const userEmail =
        userProfile?.email ||
        guestCheckoutUserSessionInfo.email ||
        localStorage.getItem(USER_EMAIL);
    const hasUser = userServiceId || userWithEmailTokenOnly;
    let buyerStatus = '';
    const activePromotion = getActivePromotion(user);
    if (hasUser) {
        const transactionStatus = getTransactionsStatus({
            transactions,
            resetStorage: userWithEmailTokenOnly,
        }); // reset local/session storage if user is coming with email token and preserve updated values
        buyerStatus = transactionStatus.buyerStatus;
    } else if (guestCheckoutUserId) {
        buyerStatus = getGuestCheckoutUserTransactionStatus();
    } else {
        const transactionStatus = getStoredTransactionsStatus();
        buyerStatus = transactionStatus.buyerStatus;
    }

    const tradeFirmId = tradeFirm?.serviceId;
    const storedTradeFirmId = getStoredTradeFirmId();
    const hasGuestCheckout = getGuestCheckoutTransactions(transactions);
    const userTradeFirmId = tradeFirmId || storedTradeFirmId;
    const tradeFirmRewardStatus = tradeFirm?.rewards?.currentProgram?.programInfo?.code;

    //Check to see if the tradeFirmId has to be updated
    if (tradeFirmId && tradeFirmId !== storedTradeFirmId) {
        setTradeFirmId({ tradeFirmId });
    }

    tracking.updateGtmPageInfo({
        checkoutStatus: getCheckoutStatus({ transactions }),
    });

    tracking.addUserAttributes({
        loginStatus: user.serviceId ? 'Logged In' : 'Logged Out',
        registrationStatus: user.everLoggedIn ? 'Registered' : 'Not Registered',
        userID,
        guestID: isMasquerading(document.cookie) ? null : getGuestId(),
        userEmail: userEmail ? md5(userEmail) : undefined,
        userEmailAddress: user.serviceId || guestCheckoutUserId ? userEmail : undefined,
        emailToken,
        userZip: userProfile?.zipCode,
        defaultCurrency: userProfile?.currency,
        registrationDate: formatDate(user.createdDate),
        currentAccountType: getCurrentAccountType({ user }),
        firstSessionDate: getFirstUtmSessionDate(user) || undefined,
        contactDealerStatus: user.totalMessagesToDealers
            ? 'dealer contacted'
            : 'no dealer contacted',
        buyerStatus,
        hasGuestCheckout,
        tradeStatus: user.verifiedTradeStatus,
        customerType: getCustomerType(user, forcedTradeUserType),
        // can tradeTier just be removed at this point?
        tradeTier: tradeFirmRewardStatus,
        tradeFirmId: userTradeFirmId,
        tradeRewardStatus: tradeFirmRewardStatus,
        profileStatus: tradeFirm?.profileStatus,
        incomeRange,
        mentionMeCiphertext: mentionMeTagParameters?.ciphertext,
        mentionMeNonce: mentionMeTagParameters?.nonce,
        activePromotion,
    });
}

// used for relay in dbl/initializeTracking,
// dbl/Header, dbl/MobileNavWrapper
export const userFragment = graphql`
    fragment userTracking_user on User {
        createdDate
        firstSessionDate
        isVip
        isVerifiedTrade
        verifiedTradeStatus
        authMetadata {
            emailOnly
            hasPassword
        }
        profile {
            email
            zipCode
            currency
        }
        serviceId
        totalMessagesToDealers
        tradeFirm {
            serviceId
            profileStatus
            rewards {
                currentProgram {
                    programInfo {
                        code
                    }
                }
            }
        }
        mentionMeTagParameters {
            ciphertext
            nonce
        }
        activePromotion {
            campaignName
            buyerDisplayable
        }
    }
`;

function getUserFragmentData(user) {
    const {
        firstSessionDate,
        isVip,
        isVerifiedTrade,
        verifiedTradeStatus,
        profile,
        serviceId,
        totalMessagesToDealers,
        tradeFirm,
        createdDate,
        authMetadata,
        mentionMeTagParameters,
        activePromotion,
    } = user;
    return {
        firstSessionDate,
        isVip,
        isVerifiedTrade,
        verifiedTradeStatus,
        profile,
        serviceId,
        totalMessagesToDealers,
        tradeFirm,
        createdDate,
        authMetadata,
        mentionMeTagParameters,
        activePromotion,
    };
}

// used for relay in
// dbl/initializeTracking, dbl/MobileNavWrapper,
// dbl/Header, abc/CheckoutConfirmationContainer
export const viewerFragment = graphql`
    fragment userTracking_viewer on Viewer {
        transactionsCounter(buyerId: $checkoutUserId) @include(if: $hasCheckoutUserId) {
            submittedTransactions: submitted @include(if: $hasUserId)
            confirmedTransactionsMostRecent: confirmed @include(if: $hasUserId)
            guestCheckoutTransactions: guest
        }
    }
`;

// used for relay in dbl/MobileNavWrapper, dbl/Header
export const regionalInfoFragment = graphql`
    fragment userTracking_regionalInfo on RegionalInfo {
        incomeLevel(zipCode: $zipCode) {
            incomeBracket
            incomePercentile
        }
    }
`;

const userTrackingQuery = graphql`
    query userTrackingQuery(
        $hasCheckoutUserId: Boolean!
        $checkoutUserId: String!
        $hasUserId: Boolean!
        $isLoggedIn: Boolean!
        $userId: String!
    ) {
        viewer {
            ...userTracking_viewer @relay(mask: false)
            user(userId: $userId) @include(if: $isLoggedIn) {
                isVerifiedTrade
            }
        }
    }
`;

function getViewerFragmentData(viewer) {
    const transactions = viewer?.transactionsCounter || {};
    const user = viewer?.user;

    const {
        submittedTransactions,
        confirmedTransactionsMostRecent,
        guestCheckoutTransactions,
    } = transactions;
    const confirmedTransactions = confirmedTransactionsMostRecent;
    const isVerifiedTrade = user?.isVerifiedTrade;
    return {
        userIsVerifiedTrade: isVerifiedTrade,
        submittedTransactions,
        confirmedTransactions,
        guestCheckoutTransactions,
    };
}

export function unmaskViewerFragmentData({ relayContext, viewer }) {
    const viewerData =
        unmaskFragmentData({
            relayContext,
            relayData: viewer,
            fragmentPropName: 'viewer',
            fragment: viewerFragment,
        }) || {};
    return getViewerFragmentData(viewerData);
}

async function getUserInfoFromEmailToken({ queryEmailToken, environment }) {
    // always make this value a string so that the `userTrackingQuery` does
    // not throw `Expected non-nullable type String! not to be null`
    const tokens = getBuyerToken(document.cookie) || {};
    const isLoggedIn = !!tokens.userToken;
    const userId = getUserIdFromEmailToken(queryEmailToken) || '';
    const checkoutUserId = userId || getGuestUserSessionInfo().userId || '';
    const transactionsData = await fetchQuery(environment, userTrackingQuery, {
        // checkout user id info is related to guest checkout
        hasCheckoutUserId: !!checkoutUserId,
        checkoutUserId,
        hasUserId: !!userId,
        // user id info is used to look up if user is verified trade
        isLoggedIn,
        userId,
    });
    const viewerData = transactionsData?.viewer;
    return getViewerFragmentData(viewerData);
}

/*
 * should be called on initial page load for all entries.
 * determines user tracking data to send to dibs-tracking.
 */
export async function init({ viewer = {}, user, environment }) {
    let transactions = getViewerFragmentData(viewer);
    let userWithEmailTokenOnly = false;
    let forcedTradeUserType = null;
    const { regionalInfo } = viewer;
    const everLoggedIn = getEverLoggedIn(document.cookie);
    const userFragmentData = user ? getUserFragmentData(user) : undefined;

    const queryEmailToken = getEmailTokenFromQuery();

    if (queryEmailToken) {
        localStorage.setItem(USER_EMAIL_TOKEN, queryEmailToken);
        /*
            fetch user's transactions from emailToken if user is not available
        */
        if (!user) {
            userWithEmailTokenOnly = true;
            const userInfo = await getUserInfoFromEmailToken({
                queryEmailToken,
                environment,
            });
            const { userIsVerifiedTrade, ...transactionsInfo } = userInfo;
            transactions = transactionsInfo;
            forcedTradeUserType = userIsVerifiedTrade;
        }
    }

    if (!getBuyerId(document.cookie)) {
        trackPreRegistrationPageVisit();
    }

    handleGuestId();
    trackUserAttributes({
        user: { ...userFragmentData, everLoggedIn },
        forcedTradeUserType,
        userWithEmailTokenOnly,
        transactions,
        regionalInfo,
    });
}
